import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Building2, CalendarDays, ClipboardList, LayoutGrid, Lock, LogOut, MapPin, ArrowLeft } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * VenuePortalLayout — the shared shell for all public /venue routes.
 * Handles the shared password gate; once verified stores the password
 * in localStorage under `venue_pw` for use by venueApi.
 */
export default function VenuePortalLayout() {
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState('');
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const existing = localStorage.getItem('venue_pw');
    if (!existing) {
      setChecking(false);
      return;
    }
    axios
      .post(`${BASE}/api/venue/verify-password`, { password: existing })
      .then(() => setReady(true))
      .catch(() => localStorage.removeItem('venue_pw'))
      .finally(() => setChecking(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      await axios.post(`${BASE}/api/venue/verify-password`, { password });
      localStorage.setItem('venue_pw', password);
      setReady(true);
      toast.success('Access granted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Incorrect password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('venue_pw');
    setReady(false);
    setPassword('');
    navigate('/venue');
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 rounded-full border-b-2 border-violet-600" />
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-indigo-50 flex items-center justify-center p-4" data-testid="venue-gate">
        <Card className="w-full max-w-md p-8 border border-border shadow-xl bg-white">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-md mb-4">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Venue Requirement Portal</h1>
            <p className="text-sm text-slate-500 mt-1.5">Enter the shared portal password to continue.</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="venue-gate-form">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500 mb-1.5 block">Portal Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                data-testid="venue-gate-password"
                autoFocus
                required
                className="rounded-md"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="venue-gate-submit"
              className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-md h-10 font-medium"
            >
              {submitting ? 'Verifying…' : 'Continue'}
            </Button>
          </form>
          <p className="text-[11px] text-slate-400 text-center mt-6">
            Sales access only — no user account required.
          </p>
        </Card>
      </div>
    );
  }

  const tabs = [
    { path: '/venue/form', label: 'New Requirement', icon: ClipboardList },
    { path: '/venue/calendar', label: 'Calendar', icon: CalendarDays },
    { path: '/venue/labs', label: 'Lab Occupancy', icon: LayoutGrid },
    { path: '/venue/list', label: 'All Requirements', icon: Building2 },
  ];

  const isTab = (p) => location.pathname === p || location.pathname.startsWith(p + '/');

  // Detect logged-in app user so we can offer a shortcut back to the main app
  const loggedInUser = (() => {
    try {
      const token = localStorage.getItem('token');
      const raw = localStorage.getItem('user');
      if (!token || !raw) return null;
      return JSON.parse(raw);
    } catch { return null; }
  })();
  const backPath = loggedInUser?.role === 'trainer'
    ? '/trainer/dashboard'
    : loggedInUser?.role === 'restricted_user'
      ? '/training/dashboard'
      : '/dashboard';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="venue-portal">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/venue" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-sm">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-slate-900 tracking-tight leading-tight">Venue Requirement Portal</h1>
              <p className="text-[11px] text-slate-500">Block labs · Track occupancy · No login needed</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            {loggedInUser && (
              <Link to={backPath} data-testid="venue-back-to-app">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs rounded-md border-violet-200 text-violet-700 hover:bg-violet-50"
                >
                  <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to App
                </Button>
              </Link>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="venue-logout"
              className="h-8 text-xs rounded-md border-slate-200 text-slate-600 hover:bg-slate-100"
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Exit Portal
            </Button>
          </div>
        </div>
        <nav className="max-w-[1400px] mx-auto px-6 flex items-center gap-1 border-t border-slate-100">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = isTab(t.path);
            return (
              <Link
                key={t.path}
                to={t.path}
                data-testid={`venue-tab-${t.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-[13px] font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-violet-600 text-violet-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </Link>
            );
          })}
        </nav>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}
